import { useState } from 'react';
import { line, Color, Size, t, type AssetAttributeAnswer } from '@bms2/shared';
import { Button, DatePicker, Select } from '../../components/controls';
import styles from './RecordPage.module.css';
import { get, patch, post, useApi } from './api';
import { workOrderLineAttributesPath } from '../assets/api';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
const WORK_ORDERS = '/work-orders';

export interface LineFieldRow {
  id: string;
  dueDate: string | null;
  assetId?: string | null;
  assetName?: string | null;
  budgetedHours?: number | null;
  actualHours?: number;
  remainingHours?: number | null;
  overrun?: boolean;
}

interface TemplatesAnswer {
  lineId: string;
  assetId: string | null;
  categoryId: string | null;
  templates: { id: string; name: string; minimumWorkers: { minimum: number | null; assigned: number; short: boolean }; attached: boolean }[];
}

/**
 * 🔒 A line's budget (decision 0730, review item T142): the hours the sale
 * budgeted, what the line's time entries add up to, and what remains. 🔨 A
 * remainder below zero reads as an overrun and is never drawn as zero.
 */
export function WorkOrderLineBudget({ row }: { row: LineFieldRow }) {
  if (row.budgetedHours === null || row.budgetedHours === undefined) return null;
  const remaining = row.remainingHours ?? 0;
  return (
    <p className={row.overrun ? styles.problem : styles.hint} data-line-budget={row.id} data-line-overrun={row.overrun ? 'true' : 'false'}>
      {line(t('workOrder.budget.budgeted'), `${row.budgetedHours} h`, '·', t('workOrder.budget.actual'), `${row.actualHours ?? 0} h`, '·')}{' '}
      {row.overrun ? line(t('workOrder.budget.overrun'), `${Math.abs(remaining)} h`) : line(t('workOrder.budget.remaining'), `${remaining} h`)}
    </p>
  );
}

/**
 * 🔒 The attributes the line's Asset carries at Visibility Work Orders (piece
 * 28, Component Attribute Panel; T2367): each one's name and value, and its
 * note where it has one. One left at Asset record or Reports is never drawn
 * here. A line naming no asset reads none, and a refused or empty read draws
 * nothing, so the line never waits on the register.
 */
export function WorkOrderLineAttributes({ lineId, apiBaseUrl = '' }: { lineId: string; apiBaseUrl?: string }) {
  const read = useApi<AssetAttributeAnswer[]>(workOrderLineAttributesPath(lineId), apiBaseUrl);
  const rows = read.state.kind === 'ready' && Array.isArray(read.state.data) ? read.state.data.filter((row) => row.visibility === 'WorkOrders') : [];
  if (rows.length === 0) return null;
  return (
    <div data-line-attributes={lineId}>
      <p className={styles.fieldLabel}>{t('asset.workOrderLine.attributes')}</p>
      <ul className={styles.plainList}>
        {rows.map((row) => (
          <li key={row.id} data-line-attribute={row.attributeName}>
            {line(`${row.attributeName}:`, row.displayValue)}
            {row.notes ? <span className={styles.hint}> {row.notes}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * 🔒 The line's own Asset, its due date and the checklists its Asset offers
 * (piece 29). The Asset is any node at the job's site, or one standing at no site under its heading (decision 12540); its Category offers the
 * templates, and a template's worker count is drawn inline as a confirmation
 * or a warning. 🔨 The count never refuses anything, dispatch included.
 */
export function WorkOrderLineField({ workOrderId, row, editable, apiBaseUrl = '', onChanged, onError }: { workOrderId: string; row: LineFieldRow; editable: boolean; apiBaseUrl?: string; onChanged: () => void; onError: (message: string) => void }) {
  const assets = useApi<{ rows: { id: string; name: string; depth: number; atSite?: boolean }[] }>(editable ? `${WORK_ORDERS}/${workOrderId}/assets` : null, apiBaseUrl);
  const [offered, setOffered] = useState<TemplatesAnswer | null>(null);
  const wrote = (outcome: { ok: true } | { ok: false; message: string }) => {
    if (outcome.ok) onChanged();
    else onError(outcome.message);
  };
  const setLine = async (body: Record<string, unknown>) => wrote(await patch(`${WORK_ORDERS}/${workOrderId}/lines/${row.id}`, body, apiBaseUrl));
  const openChecklists = async () => {
    if (offered) return setOffered(null);
    const answered = await get<TemplatesAnswer>(`${WORK_ORDERS}/${workOrderId}/lines/${row.id}/templates`, apiBaseUrl);
    if (!answered.ok) return onError(answered.message);
    setOffered(answered.data);
  };
  const attach = async (templateId: string) => {
    const answered = await post<TemplatesAnswer>(`${WORK_ORDERS}/${workOrderId}/lines/${row.id}/checklists`, { templateId }, apiBaseUrl);
    if (!answered.ok) return onError(answered.message);
    // 🔨 The line stays mounted through the reload that follows (decision 1431), so an answer missing its templates keeps the list it had rather than crashing the line.
    if (Array.isArray(answered.data?.templates)) setOffered(answered.data);
    onChanged();
  };
  // 🔨 A reply missing its rows reads as no nodes rather than crashing the line.
  const nodes = assets.state.kind === 'ready' && Array.isArray(assets.state.data?.rows) ? assets.state.data.rows : [];
  return (
    <div data-line-field={row.id}>
      {editable ? (
        <div className={styles.actions}>
          <Select
            size={Size.Medium}
            label={t('workOrder.line.asset')}
            value={row.assetId ?? ''}
            onChange={(value) => void setLine({ assetId: value || null })}
            placeholder={t('workOrder.line.noAsset')}
            // The tree reads as an indented list, each node under its parent. 🔒 Decision 12540: the job's site's nodes first, then the nodes at no site under the No site heading.
            options={nodes.map((node) => ({ value: node.id, label: `${'· '.repeat(node.depth)}${node.name}`, ...(node.atSite === false ? { group: t('asset.site.none') } : {}) }))}
            dataAttribute={{ 'data-line-asset': row.id }}
          />
          <DatePicker size={Size.Small} label={t('workOrder.line.dueDate')} value={row.dueDate ?? ''} onChange={(value) => void setLine({ dueDate: value || null })} mode="date" name={`line-due-${row.id}`} />
        </div>
      ) : (
        <p className={styles.hint}>{line(row.assetName ?? t('workOrder.line.noAsset'), '·', row.dueDate ?? t('workOrder.line.noDueDate'))}</p>
      )}
      {/* 🔒 Keyed to the Asset, so a line given another asset reads that asset's attributes afresh. */}
      {row.assetId ? <WorkOrderLineAttributes key={row.assetId} lineId={row.id} apiBaseUrl={apiBaseUrl} /> : null}
      <Button size={Size.Small} color={Color.Inactive} text={[offered ? t('workOrder.checklist.hide') : t('workOrder.checklist.show')]} actions={[() => void openChecklists()]} />
      {offered ? (
        <ul className={styles.plainList} data-line-templates={row.id}>
          {offered.templates.length === 0 ? <li className={styles.hint}>{t('workOrder.checklist.none')}</li> : null}
          {offered.templates.map((template) => (
            <li key={template.id} data-line-template={template.name} data-template-short={template.minimumWorkers.short ? 'true' : 'false'}>
              {template.name}{' '}
              {template.minimumWorkers.minimum === null ? null : (
                <span className={template.minimumWorkers.short ? styles.problem : styles.hint} data-template-workers={`${template.minimumWorkers.assigned}/${template.minimumWorkers.minimum}`}>
                  {line(template.minimumWorkers.short ? t('workOrder.checklist.workersShort') : t('workOrder.checklist.workersMet'), `${template.minimumWorkers.assigned} / ${template.minimumWorkers.minimum}`)}
                </span>
              )}{' '}
              {template.attached ? <span className={styles.hint}>{t('workOrder.checklist.attached')}</span> : editable ? <Button size={Size.Small} text={[t('workOrder.checklist.attach')]} actions={[() => void attach(template.id)]} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
